import React from 'react';
import { Link } from 'react-router-dom';
import { formatElevationMeters } from '@radevents/shared';
import { CalendarDays, CalendarPlus, Clock3, ExternalLink, Bookmark, MapPin, Mountain } from 'lucide-react';

export const EventElevation: React.FC<{ meters: number }> = ({ meters }) => (
  <span className="ed-mobile-elevation"><Mountain size={14} aria-hidden />{formatElevationMeters(meters) ?? '— hm'}</span>
);

export const EventDetailFacts: React.FC<{
  date: string; place?: string; area?: boolean; mapsUrl?: string; time?: string; participants?: string;
  /** „in 21 Tagen" — als Chip neben „Termin", statt in einer eigenen Zeile unter dem Titel. */
  countdown?: string;
  /** Der Termin ist selbst der Kalender-Link (ics), wie die Adresse der Link nach Maps. */
  calendar?: EventCalendarLink;
  /** „ca. 12 km" vom eigenen Standort — als Chip neben „Startort", wie der Countdown beim Termin. */
  distance?: string;
}> = ({ date, place, area, mapsUrl, time, participants, countdown, calendar, distance }) => (
  <div className="ed-mobile-facts">
    <div className="ed-mobile-fact"><CalendarDays size={20} aria-hidden /><div>
      <span className="ed-mobile-fact-label">Termin{countdown && <em className="ed-mobile-countdown">{countdown}</em>}</span>
      {calendar
        ? <a href={calendar.url} download={calendar.filename} onClick={calendar.onClick} aria-label={`${date}, zum Kalender hinzufügen`}><strong>{date}<CalendarPlus className="ed-fact-link-icon" size={15} aria-hidden /></strong></a>
        : <strong>{date}</strong>}
    </div></div>
    <div className="ed-mobile-fact"><MapPin size={20} aria-hidden /><div>
      <span className="ed-mobile-fact-label">{area ? 'Gebiet' : 'Startort'}{distance && <em className="ed-mobile-countdown">{distance}</em>}</span>
      {place && mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" aria-label={`${place}, in Google Maps öffnen`}><strong>{place}<ExternalLink className="ed-fact-link-icon" size={14} aria-hidden /></strong></a> : <strong>{place || 'Nicht angegeben'}</strong>}
    </div></div>
    {time && <div className="ed-mobile-fact"><Clock3 size={20} aria-hidden /><div><span>Startzeit</span><strong>{time}</strong></div></div>}
    {participants && <p className="ed-mobile-participants">Teilnehmer: {participants} (letzte Auflage)</p>}
  </div>
);

export interface EventCalendarLink { url: string; filename: string; onClick: () => void; }

/** Hauptknopf der Leiste: extern (Anmeldung, Veranstalter-Website) oder intern (Veranstalterprofil). */
export interface EventDetailPrimaryAction { label: string; href: string; internal?: boolean; onClick?: () => void; }

/**
 * Aktionsleiste unten auf dem Handy: Vormerken, Kalender, Hauptknopf (Entwurf 26.10.2026, Vorbild
 * Strava). Die kleinen Handlungen links, der Hauptknopf füllt den Rest; der Daumen erreicht ihn rechts.
 */
export const EventDetailActions: React.FC<{
  primary?: EventDetailPrimaryAction; favorited: boolean; onFavorite: () => void;
  /** Statt eines Links ein eigener Hauptknopf, etwa „Fürs nächste Jahr benachrichtigen“. */
  primaryNode?: React.ReactNode;
  /** Termin in den Kalender (ics); fehlt bei permanenten Events. */
  calendar?: EventCalendarLink;
}> = ({ primary, favorited, onFavorite, primaryNode, calendar }) => (
  <div className="ed-mobile-actions">
    <button className="ed-icon-action" aria-label={favorited ? 'Vormerkung entfernen' : 'Event vormerken'} aria-pressed={favorited} onClick={onFavorite}><Bookmark size={22} fill={favorited ? 'currentColor' : 'none'} aria-hidden /></button>
    {calendar && (
      <a className="ed-icon-action" href={calendar.url} download={calendar.filename} onClick={calendar.onClick} aria-label="Zum Kalender hinzufügen"><CalendarPlus size={22} aria-hidden /></a>
    )}
    {primaryNode ?? (primary && (primary.internal
      ? <Link className="ed-organizer-action" to={primary.href} onClick={primary.onClick}>{primary.label}</Link>
      : <a className="ed-organizer-action" href={primary.href} target="_blank" rel="noopener noreferrer" onClick={primary.onClick}><ExternalLink size={18} aria-hidden />{primary.label}</a>))}
  </div>
);
